/**
 * Taxonomy Slug Lookup Utility
 * 
 * Provides name→slug lookups for taxonomies (concepts, formats, elements, topics, accountTypes).
 * Fetches taxonomy data once and caches it to avoid redundant API calls.
 * 
 * Usage:
 *   import { getTaxonomySlug, preloadTaxonomies } from '@/utils/taxonomySlugLookup';
 *   
 *   // Optionally preload on app init
 *   await preloadTaxonomies();
 *   
 *   // Get slug for a taxonomy by name
 *   const slug = await getTaxonomySlug('concepts', 'Expert Rate/Rank');
 *   // Returns: 'expert-rating' (from database, not generated)
 */

import { videoApi } from '@/services/api.js';

// Cache for taxonomy data
let taxonomyCache = null;
let fetchPromise = null;

/**
 * Fetches and caches taxonomy data from /api/filters/public
 * @returns {Promise<Object>} Taxonomy data with name→slug maps
 */
async function fetchTaxonomies() {
  // If already cached, return immediately
  if (taxonomyCache) {
    return taxonomyCache;
  }
  
  // If fetch is in progress, wait for it
  if (fetchPromise) {
    return fetchPromise;
  }
  
  // Start fetch
  fetchPromise = (async () => {
    try {
      const response = await videoApi.getPublicFilters();
      const data = response.data?.data || {};
      
      // Build name→slug lookup maps for each taxonomy type
      taxonomyCache = {
        concepts: buildLookupMap(data.concepts || []),
        formats: buildLookupMap(data.formats || []),
        elements: buildLookupMap(data.elements || []),
        topics: buildLookupMap(data.topics || []),
        accountTypes: buildLookupMap(data.accountTypes || [])
      };
      
      return taxonomyCache;
    } catch (error) {
      console.error('Error fetching taxonomy data for slug lookup:', error);
      // Return empty maps on error - will fall back to generated slugs
      taxonomyCache = {
        concepts: {},
        formats: {},
        elements: {},
        topics: {},
        accountTypes: {}
      };
      return taxonomyCache;
    } finally {
      fetchPromise = null;
    }
  })();
  
  return fetchPromise;
}

/**
 * Builds a case-insensitive name→slug lookup map from taxonomy array
 * @param {Array} items - Array of taxonomy items with name and slug properties
 * @returns {Object} Map of lowercase name → slug
 */
function buildLookupMap(items) {
  const map = {};
  for (const item of items) {
    if (item.name && item.slug) {
      // Use lowercase name as key for case-insensitive lookup
      map[item.name.toLowerCase()] = item.slug;
    }
  }
  return map;
}

/**
 * Generates a slug from text (fallback when lookup fails)
 * @param {string} text - Text to convert to slug
 * @returns {string} Generated slug
 */
function generateSlug(text) {
  if (!text) return '';
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single
    .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
}

/**
 * Gets the canonical slug for a taxonomy by name
 * @param {string} type - Taxonomy type: 'concepts', 'formats', 'elements', 'topics', 'accountTypes'
 * @param {string} name - Display name of the taxonomy
 * @returns {Promise<string>} The canonical slug from database, or generated slug as fallback
 */
export async function getTaxonomySlug(type, name) {
  if (!name) return '';
  
  const cache = await fetchTaxonomies();
  const lookupMap = cache[type];
  
  if (lookupMap) {
    const slug = lookupMap[name.toLowerCase()];
    if (slug) {
      return slug;
    }
  }
  
  // Fallback to generated slug if not found in lookup
  console.warn(`Taxonomy slug not found for ${type}:"${name}", using generated slug`);
  return generateSlug(name);
}

/**
 * Synchronous version - returns from cache or generates slug
 * Use this when you can't use async (e.g., in computed properties)
 * Call preloadTaxonomies() first to ensure cache is populated
 * @param {string} type - Taxonomy type
 * @param {string} name - Display name
 * @returns {string} Slug from cache or generated
 */
export function getTaxonomySlugSync(type, name) {
  if (!name) return '';
  
  if (taxonomyCache && taxonomyCache[type]) {
    const slug = taxonomyCache[type][name.toLowerCase()];
    if (slug) {
      return slug;
    }
  }
  
  // Fallback to generated slug
  return generateSlug(name);
}

// Returns only a database-provided slug. This is used for public links where a
// guessed fallback could create a crawlable noncanonical URL.
export function getCachedTaxonomySlugSync(type, name) {
  if (!name || !taxonomyCache?.[type]) return '';
  return taxonomyCache[type][name.toLowerCase()] || '';
}

/**
 * Preloads taxonomy data into cache
 * Call this on app initialization to avoid async lookups later
 * @returns {Promise<void>}
 */
export async function preloadTaxonomies() {
  await fetchTaxonomies();
}

/**
 * Clears the taxonomy cache (useful for testing or after taxonomy updates)
 */
export function clearTaxonomyCache() {
  taxonomyCache = null;
  fetchPromise = null;
}
